<template>
  <div>
    <TitlePage title="Classifiche"></TitlePage>

    <v-card tile class="mb-4" flat>
      <v-toolbar flat dense>
        <v-toolbar-title class="subtitle-2 font-weight-bold">Classifica salvata</v-toolbar-title>
        <v-spacer />
        <v-btn small text color="primary" to="/match-stats">
          <v-icon left small>mdi-chart-bar</v-icon>
          Statistiche partite
        </v-btn>
        <v-btn small text color="warning" to="/match-stats/user-override">
          <v-icon left small>mdi-account-edit</v-icon>
          Statistiche utente
        </v-btn>
      </v-toolbar>
      <v-divider />
      <v-row class="px-4 pt-3 pb-2 align-center">
        <v-col cols="12" md="4">
          <v-select
              v-model="filters.sport_master"
              :items="sportItems"
              item-text="text"
              item-value="value"
              label="Sport master"
              dense
              outlined
              hide-details
              clearable
              @change="fetchRows"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model.number="filters.limit"
            label="Limite"
            dense
            outlined
            hide-details
            type="number"
            min="1"
          />
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end">
          <v-btn color="primary" depressed class="mr-2" @click="fetchRows">
            <v-icon left>mdi-magnify</v-icon>
            Cerca
          </v-btn>
          <v-btn color="warning" depressed :disabled="!filters.sport_master" @click="rebuild">
            <v-icon left>mdi-refresh</v-icon>
            Ricalcola sport
          </v-btn>
          <v-btn color="error" depressed class="ml-2" :loading="rebuildingAll" @click="rebuildAll">
            <v-icon left>mdi-refresh-circle</v-icon>
            Ricalcola tutti gli sport
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="rows"
      item-key="_id"
      class="elevation-1"
      :loading="loading"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:item.user="{ item }">
        {{ displayName(item) }}
        <div class="caption text--secondary">{{ item.user_id }}</div>
      </template>

      <template v-slot:item.stats="{ item }">
        <v-chip
          v-for="(value, key) in item.stats"
          :key="key"
          x-small
          class="mr-1 mb-1"
        >
          {{ key }}: {{ value }}
        </v-chip>
      </template>

      <template v-slot:item.action="{ item }">
        <v-btn icon small color="primary" @click="openEdit(item)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="860">
      <v-card v-if="editItem">
        <v-card-title class="subtitle-1 font-weight-bold">
          Modifica classifica - {{ displayName(editItem) }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              v-for="field in numericFields"
              :key="field.value"
              cols="6"
              md="3"
            >
              <v-text-field
                v-model.number="editForm[field.value]"
                :label="field.text"
                dense
                outlined
                type="number"
                hide-details
              />
            </v-col>
          </v-row>

          <div class="subtitle-2 font-weight-bold mt-4 mb-2">Statistiche salvate</div>
          <v-row>
            <v-col
              v-for="(value, key) in editForm.stats"
              :key="key"
              cols="6"
              md="3"
            >
              <v-text-field
                v-model.number="editForm.stats[key]"
                :label="key"
                dense
                outlined
                type="number"
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Annulla</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="saveEdit">
            <v-icon left small>mdi-content-save</v-icon>
            Salva
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Chiudi</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import TitlePage from '@/components/utils/TitlePage';

export default {
  name: 'MatchLeaderboard',
  components: { TitlePage },
  data() {
    return {
      loading: false,
      saving: false,
      rebuildingAll: false,
      dialog: false,
      rows: [],
      editItem: null,
      editForm: {},
      sportConfigs: [],
      filters: {
        sport_master: '',
        limit: 200,
      },
      numericFields: [
        { text: 'Posizione', value: 'rank' },
        { text: 'Punteggio', value: 'score' },
        { text: 'Partite giocate', value: 'matches_played' },
        { text: 'MVP', value: 'mvp_received' },
        { text: 'Vittorie', value: 'wins' },
        { text: 'Pareggi', value: 'draws' },
        { text: 'Sconfitte', value: 'losses' },
      ],
      headers: [
        { text: 'Sport', value: 'sport_master' },
        { text: 'Pos.', value: 'rank' },
        { text: 'Punti', value: 'score' },
        { text: 'Utente', value: 'user' },
        { text: 'PG', value: 'matches_played' },
        { text: 'MVP', value: 'mvp_received' },
        { text: 'V', value: 'wins' },
        { text: 'P', value: 'draws' },
        { text: 'S', value: 'losses' },
        { text: 'Stats', value: 'stats', sortable: false },
        { text: 'Azioni', value: 'action', sortable: false },
      ],
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    sportItems() {
      return this.sportConfigs
          .filter(item => item && item.sport_master && item.active !== false)
          .map(item => ({ text: item.sport_master, value: item.sport_master }));
    },
  },
  async created() {
    await this.loadSports();
    await this.fetchRows();
  },
  methods: {
    ...mapActions({
      adminGetLeaderboard: 'matchStats/adminGetLeaderboard',
      adminUpdateLeaderboardRow: 'matchStats/adminUpdateLeaderboardRow',
      adminRebuildLeaderboard: 'matchStats/adminRebuildLeaderboard',
      adminRebuildAllLeaderboards: 'matchStats/adminRebuildAllLeaderboards',
      getSportConfigs: 'sportMasterConfig/index',
    }),
    async loadSports() {
      const result = await this.getSportConfigs({ active: true });
      this.sportConfigs = Array.isArray(result) ? result : [];
    },
    async fetchRows() {
      this.loading = true;
      try {
        const result = await this.adminGetLeaderboard({
          sport_master: this.filters.sport_master,
          limit: this.filters.limit,
        });
        this.rows = (result && result.rows) || [];
      } catch {
        this.toast('Errore durante il caricamento della classifica', 'error');
      } finally {
        this.loading = false;
      }
    },
    openEdit(item) {
      this.editItem = item;
      this.editForm = {
        rank: item.rank,
        score: item.score,
        matches_played: item.matches_played,
        mvp_received: item.mvp_received,
        wins: item.wins,
        draws: item.draws,
        losses: item.losses,
        stats: { ...(item.stats || {}) },
      };
      this.dialog = true;
    },
    async saveEdit() {
      if (!this.editItem) return;
      this.saving = true;
      try {
        await this.adminUpdateLeaderboardRow({
          entryId: this.editItem._id,
          payload: this.editForm,
        });
        this.toast('Classifica aggiornata', 'success');
        this.dialog = false;
        await this.fetchRows();
      } catch {
        this.toast('Errore durante il salvataggio', 'error');
      } finally {
        this.saving = false;
      }
    },
    async rebuild() {
      this.loading = true;
      try {
        const result = await this.adminRebuildLeaderboard(this.filters.sport_master);
        const rebuilt = result && result.result ? result.result.rebuilt : 0;
        this.toast(`Classifica ricalcolata. ${rebuilt} righe salvate.`, 'success');
        await this.fetchRows();
      } catch {
        this.toast('Errore durante il ricalcolo', 'error');
      } finally {
        this.loading = false;
      }
    },
    async rebuildAll() {
      this.rebuildingAll = true;
      try {
        const result = await this.adminRebuildAllLeaderboards();
        const sports = result && result.results ? Object.keys(result.results).length : 0;
        this.toast(`Ricalcolo completato — ${sports} sport aggiornati`, 'success');
        await this.fetchRows();
      } catch {
        this.toast('Errore durante il ricalcolo globale', 'error');
      } finally {
        this.rebuildingAll = false;
      }
    },
    displayName(item) {
      const user = item.user || {};
      return [user.name, user.surname].filter(Boolean).join(' ') || 'Utente';
    },
    toast(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>
